'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { ConnectorManager } from '../ConnectorManager'
import { useSearchParams, usePathname } from 'next/navigation'

interface TradeHistoryItem {
  symbol: string
  id: number
  orderId: number
  orderListId: number
  price: string
  qty: string
  quoteQty: string
  commission: string
  commissionAsset: string
  time: number
  isBuyer: boolean
  isMaker: boolean
  isBestMatch: boolean
}

// Common trading pairs for the dropdown
const COMMON_PAIRS = [
  'BTCUSDT',
  'ETHUSDT',
  'BNBUSDT',
  'ADAUSDT',
  'DOGEUSDT',
  'XRPUSDT',
  'DOTUSDT',
  'UNIUSDT',
  'LTCUSDT',
  'LINKUSDT',
  'BTCBUSD',
  'ETHBTC',
  'BNBBTC'
]

export default function TradeHistory() {
  const searchParams = useSearchParams()
  // const urlSymbol = searchParams?.get('symbol');
  const pathname = usePathname()

  // Get symbol from URL path instead of query parameters
  const pathSymbol = pathname?.split('/').pop()?.toUpperCase()

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [trades, setTrades] = useState<TradeHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || 'BTCUSDT')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const connectorManager = ConnectorManager.getInstance()

  useEffect(() => {
    fetchTradeHistory(selectedSymbol)
  }, [selectedSymbol])

  const fetchTradeHistory = async (symbol: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Get the appropriate connector based on route
      // If on /x/ routes, default to ninx
      const isOnXRoute = pathname?.startsWith('/x/')
      const connectorName = isOnXRoute ? 'ninx' : 'binance'
      const binanceConnector = connectorManager.getConnector(connectorName)

      if (!binanceConnector) {
        throw new Error('Connector not found or not connected')
      }

      // Fetch trade history from the connector with the specified symbol
      const tradesData = await binanceConnector.getTradeHistory(symbol)

      // Sort trades by time, most recent first
      const sortedTrades = [...tradesData].sort((a, b) => b.time - a.time)
      setTrades(sortedTrades)
    } catch (err: any) {
      console.error('Error fetching trade history:', err)
      setError(
        err.message ||
          'Failed to fetch trade history. Please ensure you are connected to Binance.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }

  const handleCustomSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading trade history...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_PAIRS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_PAIRS.map(pair => (
              <option key={pair} value={pair}>
                {pair}
              </option>
            ))}
            <option value="custom">Custom Pair</option>
          </select>

          {(showCustomInput || !COMMON_PAIRS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_PAIRS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. SOLUSDT)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          Current: <strong>{selectedSymbol}</strong>
        </div>
      </div>

      {trades.length === 0 ? (
        <div className={styles.emptyState}>
          No trade history found for {selectedSymbol}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Amount</th>
                <th>Total</th>
                <th>Fee</th>
              </tr>
            </thead>
            <tbody>
              {trades.map(trade => {
                const date = new Date(trade.time)
                const formattedDate = date.toLocaleString()
                const price = parseFloat(trade.price)
                const amount = parseFloat(trade.qty)
                const total = price * amount
                const fee = parseFloat(trade.commission)

                return (
                  <tr key={trade.id}>
                    <td>{formattedDate}</td>
                    <td>{trade.symbol}</td>
                    <td>{trade.isMaker ? 'LIMIT' : 'MARKET'}</td>
                    <td
                      className={
                        trade.isBuyer ? styles.buyOrder : styles.sellOrder
                      }
                    >
                      {trade.isBuyer ? 'BUY' : 'SELL'}
                    </td>
                    <td>${price.toFixed(price < 1 ? 8 : 2)}</td>
                    <td>{amount.toFixed(8)}</td>
                    <td>${total.toFixed(2)}</td>
                    <td>
                      {fee.toFixed(8)} {trade.commissionAsset}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
